import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { signInWithCustomToken, getIdToken } from 'firebase/auth';

import { AppDispatch } from '../../store/store';
import { setAuthenticated } from '../../store/authSlice';
import { auth } from '../../config/firebase';
import { verifyJwtAndGetCustomToken } from '../../services/jwtService';
import { readAuthTokenFromUrl, removeAuthTokenFromUrl } from '../../lib/auth/urlAuthToken';
import { User, UserRole } from '../../types/user';

const APP_ID = 'livematch';
const LOCAL_STORAGE_KEY = `fc_auth_${APP_ID}`;

const mapRole = (role: string): UserRole => {
  switch (role) {
    case 'admin':
      return UserRole.ADMIN;
    case 'coach':
      return UserRole.COACH;
    default:
      return UserRole.USER;
  }
};

type Props = {
  children: React.ReactNode;
};

const buildUserFromJwtResponse = (response: {
  user: { uid: string; email: string; role: string };
}): User => {
  return {
    id: response.user.uid,
    email: response.user.email,
    firstName: '',
    lastName: '',
    role: mapRole(response.user.role),
    status: 'active',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

const JwtAuthBootstrap: React.FC<Props> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const { token } = readAuthTokenFromUrl();
    return !!token || !!localStorage.getItem(LOCAL_STORAGE_KEY);
  });
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      const { token, paramName } = readAuthTokenFromUrl();
      const persistedToken = localStorage.getItem(LOCAL_STORAGE_KEY);
      const jwt = token || persistedToken;
      if (!jwt) return;

      if (token && paramName) {
        removeAuthTokenFromUrl(paramName);
        localStorage.setItem(LOCAL_STORAGE_KEY, token);
      }

      setIsBootstrapping(true);
      setBootstrapError(null);

      try {
        const response = await verifyJwtAndGetCustomToken(jwt);

        const credential = await signInWithCustomToken(auth, response.customToken);
        const idToken = await getIdToken(credential.user, true);

        const user = buildUserFromJwtResponse(response);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('token', idToken);
        dispatch(setAuthenticated({ user, token: idToken }));
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Erreur inconnue';
        console.error('[JwtAuthBootstrap] Échec du flux JWT:', e);
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        setBootstrapError(message);
      } finally {
        setIsBootstrapping(false);
      }
    };

    run();
  }, [dispatch]);

  if (!isBootstrapping) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="w-full max-w-md bg-white rounded-lg shadow-md p-6">
        <div className="text-lg font-semibold text-gray-900">On prépare votre session…</div>
        <div className="mt-3 h-2 w-full rounded bg-gray-200 overflow-hidden">
          <div className="h-full w-2/3 bg-blue-600 animate-pulse" />
        </div>
        <div className="mt-3 text-sm text-gray-600">Quelques secondes, nous sécurisons votre accès LiveMatch.</div>
        {bootstrapError && (
          <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
            {bootstrapError}
          </div>
        )}
      </div>
    </div>
  );
};

export default JwtAuthBootstrap;
